import { useEffect, useState, useCallback, useMemo, memo } from "react";
import MemoInput from "../../../components/MemoInput";

const DOW_LABEL = {
  monday: "月",
  tuesday: "火",
  wednesday: "水",
  thursday: "木",
  friday: "金",
  saturday: "土",
  sunday: "日",
};

const STATUS_LABEL = {
  active: "在籍",
  pause: "休会",
  cancel: "退会",
};

const COUPON_LABEL = { write: "記入する", skip: "記入しない" };
const BANK_LABEL = { net: "ネット", other: "その他" };

// 新規追加モードの下書き初期値。必須は姓・名のみで、他は空のまま登録できる
// （API 側も同様に任意。旧名簿取込データと整合させるため）。
const EMPTY_DRAFT = {
  last_name: "",
  first_name: "",
  last_kana: "",
  first_kana: "",
  birth_date: "",
  grade: "",
  school_name: "",
  guardian_last_name: "",
  guardian_first_name: "",
  email: "",
  phone_number: "",
  postal_code: "",
  address: "",
  studio_id: "",
  course_name: "",
  lesson_start_date: "",
  coupon_choice: "",
  bank_method: "",
  final_price: "",
  memo: "",
  status: "active",
  is_kuchikomi: false,
  is_bank_registered: false,
  lessons: [],
};

// 新規モードで customer prop に渡すダミー。パネルは customer 前提で描画されるため。
const NEW_CUSTOMER = { id: null, lessons: [] };

// 表示用の純粋関数（モジュールレベル＝毎レンダー再生成されず、行の memo 化を有効にする）
const calcAge = (birthDate) => {
  if (!birthDate) return "";
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const formatIdWithZeroPadding = (id, digits = 4) =>
  String(id).padStart(digits, "0");

const CustomerDetail = memo(function CustomerDetail({
  customer,
  isNew = false,
  studios,
  lessonOptions,
  onClose,
  onSave,
  onCreate,
  onStudioChange,
  onAddLesson,
  onDeleteLesson,
  formatDate,
  calcAge,
}) {
  // 新規モードは最初から編集状態＝空の下書きで開く
  const [isEditing, setIsEditing] = useState(isNew);
  const [draft, setDraft] = useState(isNew ? { ...EMPTY_DRAFT } : null);
  const [saving, setSaving] = useState(false);
  const [addingPlacementId, setAddingPlacementId] = useState("");

  useEffect(() => {
    // 新規モードは親が key で作り直すため、ここでリセットすると開いた直後に編集状態を潰す
    if (isNew) return;
    setIsEditing(false);
    setDraft(null);
    setAddingPlacementId("");
  }, [customer?.id, isNew]);

  if (!customer) return null;

  const current = isEditing ? draft : customer;

  const startEdit = () => {
    setDraft({ ...customer });
    setIsEditing(true);
  };

  const cancelEdit = () => {
    // 新規は戻る先が無いのでパネルごと閉じる
    if (isNew) {
      onClose();
      return;
    }
    setDraft(null);
    setIsEditing(false);
  };

  const updateDraft = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    // 新規時のレッスン候補は下書きの店舗で引くため親へ通知
    if (isNew && field === "studio_id") onStudioChange?.(value);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        last_name: draft.last_name,
        first_name: draft.first_name,
        last_kana: draft.last_kana,
        first_kana: draft.first_kana,
        birth_date: draft.birth_date,
        grade: draft.grade,
        school_name: draft.school_name || "",
        guardian_last_name: draft.guardian_last_name,
        guardian_first_name: draft.guardian_first_name,
        email: draft.email,
        phone_number: draft.phone_number,
        postal_code: draft.postal_code,
        address: draft.address,
        studio_id: draft.studio_id ? parseInt(draft.studio_id) : null,
        course_name: draft.course_name,
        lesson_start_date: draft.lesson_start_date,
        coupon_choice: draft.coupon_choice,
        bank_method: draft.bank_method,
        final_price:
          draft.final_price === "" || draft.final_price == null
            ? null
            : parseInt(draft.final_price, 10) || 0,
        memo: draft.memo || "",
        status: draft.status || "active",
        is_kuchikomi: !!draft.is_kuchikomi,
        is_bank_registered: !!draft.is_bank_registered,
      };
      if (isNew) {
        // 下書きに溜めたクラスを placement_id の配列で同送し、API 側で会員と一括作成する
        await onCreate({
          ...payload,
          lessons: (draft.lessons || []).map((l) => l.placement_id),
        });
        return; // 成功時は親がパネルを閉じる
      }
      await onSave(customer.id, payload);
      setIsEditing(false);
      setDraft(null);
    } catch (e) {
      alert(`${isNew ? "登録" : "保存"}に失敗しました: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddLesson = async () => {
    if (!addingPlacementId) return;
    const placementId = parseInt(addingPlacementId);
    // 新規は customer_id がまだ無いので API を呼ばず下書きへ溜める（登録時にまとめて作成）
    if (isNew) {
      const opt = (lessonOptions || []).find(
        (o) => o.placement_id === placementId,
      );
      if (opt) {
        setDraft((prev) => ({
          ...prev,
          lessons: [...(prev.lessons || []), { ...opt, id: `draft-${placementId}` }],
        }));
      }
      setAddingPlacementId("");
      return;
    }
    try {
      await onAddLesson(customer.id, placementId);
      setAddingPlacementId("");
    } catch (e) {
      alert(`レッスン追加に失敗しました: ${e.message}`);
    }
  };

  const handleDeleteLesson = async (lessonId) => {
    if (isNew) {
      setDraft((prev) => ({
        ...prev,
        lessons: (prev.lessons || []).filter((l) => l.id !== lessonId),
      }));
      return;
    }
    if (!window.confirm("このレッスンを削除しますか？")) return;
    try {
      await onDeleteLesson(customer.id, lessonId);
    } catch (e) {
      alert(`レッスン削除に失敗しました: ${e.message}`);
    }
  };

  const fullName = `${current.last_name || ""} ${current.first_name || ""}`;
  const status = current.status || "active";
  const lessons = isNew ? draft.lessons || [] : customer.lessons || [];
  // 姓・名が空のうちは登録させない（API の 400 をフロントでも先回り）
  const canSubmit =
    !isNew ||
    (!!(draft.last_name || "").trim() && !!(draft.first_name || "").trim());

  // 既に追加済みの placement_id は select から除外
  const existingPlacementIds = new Set(lessons.map((l) => l.placement_id));
  const availableOptions = (lessonOptions || []).filter(
    (o) => !existingPlacementIds.has(o.placement_id),
  );

  return (
    <div className={`customer-detail-panel ${isEditing ? "editing" : ""}`}>
      <div className="customer-detail-header">
        <p>
          {isNew ? fullName.trim() || "新規会員" : fullName}{" "}
          <span className={`status-badge status-${status}`}>
            {STATUS_LABEL[status] || status}
          </span>
          {isNew ? (
            <span className="editing-badge">新規</span>
          ) : (
            isEditing && <span className="editing-badge">編集中</span>
          )}
        </p>
        <div className="header-actions">
          {!isEditing && (
            <button className="edit-btn" onClick={startEdit}>
              編集
            </button>
          )}
          {isEditing && (
            <>
              <button
                className="edit-btn"
                onClick={cancelEdit}
                disabled={saving}
              >
                キャンセル
              </button>
              <button
                className="edit-btn primary"
                onClick={handleSave}
                disabled={saving || !canSubmit}
              >
                {saving
                  ? isNew
                    ? "登録中..."
                    : "保存中..."
                  : isNew
                    ? "登録"
                    : "保存"}
              </button>
            </>
          )}
          <button onClick={onClose} aria-label="閉じる">
            <i className="fa fa-xmark"></i>
          </button>
        </div>
      </div>
      <div className="customer-detail-body">
        {isEditing ? (
          <>
            <dl>
              <dt>
                姓{isNew && <span className="req">*</span>}
              </dt>
              <dd>
                <input
                  type="text"
                  value={draft.last_name || ""}
                  onChange={(e) => updateDraft("last_name", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>
                名{isNew && <span className="req">*</span>}
              </dt>
              <dd>
                <input
                  type="text"
                  value={draft.first_name || ""}
                  onChange={(e) => updateDraft("first_name", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>せい</dt>
              <dd>
                <input
                  type="text"
                  value={draft.last_kana || ""}
                  onChange={(e) => updateDraft("last_kana", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>めい</dt>
              <dd>
                <input
                  type="text"
                  value={draft.first_kana || ""}
                  onChange={(e) => updateDraft("first_kana", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>生年月日</dt>
              <dd>
                <input
                  type="date"
                  value={draft.birth_date || ""}
                  onChange={(e) => updateDraft("birth_date", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>学年</dt>
              <dd>
                <input
                  type="text"
                  value={draft.grade || ""}
                  onChange={(e) => updateDraft("grade", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>学校名</dt>
              <dd>
                <input
                  type="text"
                  value={draft.school_name || ""}
                  onChange={(e) => updateDraft("school_name", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>保護者姓</dt>
              <dd>
                <input
                  type="text"
                  value={draft.guardian_last_name || ""}
                  onChange={(e) =>
                    updateDraft("guardian_last_name", e.target.value)
                  }
                />
              </dd>
            </dl>
            <dl>
              <dt>保護者名</dt>
              <dd>
                <input
                  type="text"
                  value={draft.guardian_first_name || ""}
                  onChange={(e) =>
                    updateDraft("guardian_first_name", e.target.value)
                  }
                />
              </dd>
            </dl>
            <dl>
              <dt>メール</dt>
              <dd>
                <input
                  type="email"
                  value={draft.email || ""}
                  onChange={(e) => updateDraft("email", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>電話</dt>
              <dd>
                <input
                  type="tel"
                  value={draft.phone_number || ""}
                  onChange={(e) => updateDraft("phone_number", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>郵便番号</dt>
              <dd>
                <input
                  type="text"
                  value={draft.postal_code || ""}
                  onChange={(e) => updateDraft("postal_code", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>住所</dt>
              <dd>
                <textarea
                  rows={2}
                  value={draft.address || ""}
                  onChange={(e) => updateDraft("address", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>店舗</dt>
              <dd>
                <select
                  value={draft.studio_id || ""}
                  onChange={(e) => updateDraft("studio_id", e.target.value)}
                >
                  <option value="">（未選択）</option>
                  {studios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </dd>
            </dl>
            <dl>
              <dt>コース</dt>
              <dd>
                <input
                  type="text"
                  value={draft.course_name || ""}
                  onChange={(e) => updateDraft("course_name", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>開始日</dt>
              <dd>
                <input
                  type="date"
                  value={draft.lesson_start_date || ""}
                  onChange={(e) =>
                    updateDraft("lesson_start_date", e.target.value)
                  }
                />
              </dd>
            </dl>
            <dl>
              <dt>クーポン</dt>
              <dd>
                <select
                  value={draft.coupon_choice || ""}
                  onChange={(e) =>
                    updateDraft("coupon_choice", e.target.value)
                  }
                >
                  {/* 空一致の選択肢が無いとブラウザが先頭を表示し、保存値('')と食い違う */}
                  <option value="">（未選択）</option>
                  <option value="write">記入する</option>
                  <option value="skip">記入しない</option>
                </select>
              </dd>
            </dl>
            <dl>
              <dt>口座登録</dt>
              <dd>
                <select
                  value={draft.bank_method || ""}
                  onChange={(e) => updateDraft("bank_method", e.target.value)}
                >
                  <option value="">（未選択）</option>
                  <option value="net">ネット</option>
                  <option value="other">その他</option>
                </select>
              </dd>
            </dl>
            <dl>
              <dt>初回料金</dt>
              <dd>
                <input
                  type="number"
                  min="0"
                  value={draft.final_price ?? ""}
                  onChange={(e) => updateDraft("final_price", e.target.value)}
                />
              </dd>
            </dl>
            <dl>
              <dt>口コミ済</dt>
              <dd>
                <input
                  type="checkbox"
                  checked={!!draft.is_kuchikomi}
                  onChange={(e) =>
                    updateDraft("is_kuchikomi", e.target.checked)
                  }
                />
              </dd>
            </dl>
            <dl>
              <dt>口座登録済</dt>
              <dd>
                <input
                  type="checkbox"
                  checked={!!draft.is_bank_registered}
                  onChange={(e) =>
                    updateDraft("is_bank_registered", e.target.checked)
                  }
                />
              </dd>
            </dl>
            <dl>
              <dt>ステータス</dt>
              <dd>
                <select
                  value={draft.status || "active"}
                  onChange={(e) => updateDraft("status", e.target.value)}
                >
                  <option value="active">在籍</option>
                  <option value="pause">休会</option>
                  <option value="cancel">退会</option>
                </select>
              </dd>
            </dl>
            <dl>
              <dt>メモ</dt>
              <dd>
                <textarea
                  rows={3}
                  value={draft.memo || ""}
                  onChange={(e) => updateDraft("memo", e.target.value)}
                  placeholder="自由メモ"
                />
              </dd>
            </dl>
            <dl>
              <dt>申込日</dt>
              <dd>{formatDate(customer.created_at) || "―"}</dd>
            </dl>
          </>
        ) : (
          <>
            <dl>
              <dt>ふりがな</dt>
              <dd>
                {customer.last_kana} {customer.first_kana}
              </dd>
            </dl>
            <dl>
              <dt>生年月日</dt>
              <dd>
                {formatDate(customer.birth_date)}（{calcAge(customer.birth_date)}
                歳）
              </dd>
            </dl>
            <dl>
              <dt>学年</dt>
              <dd>{customer.grade}</dd>
            </dl>
            <dl>
              <dt>学校名</dt>
              <dd>{customer.school_name || "―"}</dd>
            </dl>
            <dl>
              <dt>保護者</dt>
              <dd>
                {customer.guardian_last_name} {customer.guardian_first_name}
              </dd>
            </dl>
            <dl>
              <dt>メール</dt>
              <dd>
                {customer.email ? (
                  <a href={`mailto:${customer.email}`}>{customer.email}</a>
                ) : (
                  "-"
                )}
              </dd>
            </dl>
            <dl>
              <dt>電話</dt>
              <dd>
                {customer.phone_number ? (
                  <a href={`tel:${customer.phone_number}`}>
                    {customer.phone_number}
                  </a>
                ) : (
                  "-"
                )}
              </dd>
            </dl>
            <dl>
              <dt>郵便番号</dt>
              <dd>{customer.postal_code}</dd>
            </dl>
            <dl>
              <dt>住所</dt>
              <dd>{customer.address}</dd>
            </dl>
            <dl>
              <dt>店舗</dt>
              <dd>{customer.studio_name}</dd>
            </dl>
            <dl>
              <dt>コース</dt>
              <dd>{customer.course_name}</dd>
            </dl>
            <dl>
              <dt>開始日</dt>
              <dd>{formatDate(customer.lesson_start_date)}</dd>
            </dl>
            <dl>
              <dt>クーポン</dt>
              <dd>{COUPON_LABEL[customer.coupon_choice] || customer.coupon_choice}</dd>
            </dl>
            <dl>
              <dt>口座登録</dt>
              <dd>{BANK_LABEL[customer.bank_method] || customer.bank_method}</dd>
            </dl>
            <dl>
              <dt>初回料金</dt>
              <dd>
                {customer.final_price == null
                  ? "-"
                  : `¥${customer.final_price.toLocaleString()}`}
              </dd>
            </dl>
            <dl>
              <dt>メモ</dt>
              <dd>
                {customer.memo ? (
                  <span style={{ whiteSpace: "pre-wrap" }}>{customer.memo}</span>
                ) : (
                  "―"
                )}
              </dd>
            </dl>
            <dl>
              <dt>申込日</dt>
              <dd>{formatDate(customer.created_at)}</dd>
            </dl>
          </>
        )}

        <div className="customer-lesson-section">
          <div className="section-ttl">申込レッスン（{lessons.length}クラス）</div>
          {lessons.length === 0 && (
            <p className="empty-msg">申込レッスンがありません</p>
          )}
          <ul className="customer-lessons">
            {lessons.map((l) => (
              <li key={l.id} className="lesson-item">
                <div className="info">
                  <div className="row1">
                    {DOW_LABEL[l.day_of_week] || l.day_of_week}{" "}
                    {l.start_time}–{l.end_time} / {l.content_name}
                  </div>
                  <div className="row2">
                    {l.instructor_name && `講師：${l.instructor_name}`}
                    {l.studio_name && ` / ${l.studio_name}`}
                    {l.target && ` / ${l.target}`}
                  </div>
                </div>
                {isEditing && (
                  <button
                    className="remove-lesson"
                    onClick={() => handleDeleteLesson(l.id)}
                    aria-label="削除"
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
          {isNew && !draft.studio_id && (
            <p className="empty-msg">店舗を選ぶとクラスを追加できます</p>
          )}
          {isEditing && (
            <div className="add-lesson-form">
              <select
                value={addingPlacementId}
                onChange={(e) => setAddingPlacementId(e.target.value)}
              >
                <option value="">＋ 追加するクラスを選択…</option>
                {availableOptions.map((o) => (
                  <option key={o.placement_id} value={o.placement_id}>
                    {DOW_LABEL[o.day_of_week] || o.day_of_week}{" "}
                    {o.start_time} {o.instructor_name} {o.content_name}（
                    {o.studio_name}）
                  </option>
                ))}
              </select>
              <button
                className="edit-btn primary"
                onClick={handleAddLesson}
                disabled={!addingPlacementId}
              >
                追加
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// 一覧の各行。selectedCustomer 変更で全行が再描画されるのを防ぐため memo 化。
// props(customer/各ハンドラ)が変わらない行は再描画をスキップする。
const CustomerRow = memo(function CustomerRow({
  customer,
  displayNo,
  onRowClick,
  onPatch,
  onUpdateMemo,
  onDelete,
}) {
  const hasCoupon = customer.coupon_choice === "write";
  const hasNetBank = customer.bank_method === "net";
  const couponLabel =
    COUPON_LABEL[customer.coupon_choice] || customer.coupon_choice;
  const bankLabel = BANK_LABEL[customer.bank_method] || customer.bank_method;
  const status = customer.status || "active";
  const lessonCount = (customer.lessons || []).length;
  const stopRow = (e) => e.stopPropagation();
  return (
    <ul
      className="row-clickable"
      data-customer-id={customer.id}
      onClick={() => onRowClick(customer)}
    >
      <li className="no-cell" data-verify-no={displayNo}>
        {displayNo}
      </li>
      <li className="id-cell">{formatIdWithZeroPadding(customer.id)}</li>
      <li>
        {customer.last_name} {customer.first_name}
        {status !== "active" && (
          <span className={`status-badge status-${status}`}>
            {STATUS_LABEL[status]}
          </span>
        )}
      </li>
      <li>
        {customer.last_kana} {customer.first_kana}
      </li>
      <li>{calcAge(customer.birth_date)}</li>
      <li>{customer.studio_name}</li>
      <li>
        {customer.course_name}
        {lessonCount > 0 && (
          <span className="class-count-badge">{lessonCount}</span>
        )}
      </li>
      <li>{formatDate(customer.lesson_start_date)}</li>
      <li>
        {customer.final_price == null
          ? "-"
          : `¥${customer.final_price.toLocaleString()}`}
      </li>
      <li>{formatDate(customer.created_at)}</li>
      <li>{couponLabel}</li>
      <li className="icon-area" onClick={stopRow}>
        {hasCoupon && (
          <input
            type="checkbox"
            checked={customer.is_kuchikomi || false}
            onChange={async (e) => {
              try {
                await onPatch(customer.id, {
                  is_kuchikomi: e.target.checked,
                });
              } catch {
                e.target.checked = customer.is_kuchikomi;
              }
            }}
          />
        )}
      </li>
      <li>{bankLabel}</li>
      <li className="icon-area" onClick={stopRow}>
        {hasNetBank && (
          <input
            type="checkbox"
            checked={customer.is_bank_registered || false}
            onChange={async (e) => {
              try {
                await onPatch(customer.id, {
                  is_bank_registered: e.target.checked,
                });
              } catch {
                e.target.checked = customer.is_bank_registered;
              }
            }}
          />
        )}
      </li>
      <li onClick={stopRow}>
        <MemoInput
          customerId={customer.id}
          initialValue={customer.memo || ""}
          onSave={onUpdateMemo}
        />
      </li>
      <li className="icon-area" onClick={stopRow}>
        {customer.email ? (
          <a href={`mailto:${customer.email}`}>
            <i className="fa fa-envelope" aria-label="メール"></i>
          </a>
        ) : (
          <span className="no-mail" aria-label="メール未登録">
            -
          </span>
        )}
      </li>
      <li className="icon-area" onClick={stopRow}>
        {customer.phone_number ? (
          <a href={`tel:${customer.phone_number}`}>
            <i className="fa fa-phone" aria-label="電話"></i>
          </a>
        ) : (
          <span className="no-tel" aria-label="電話未登録">
            -
          </span>
        )}
      </li>
      <li className="icon-area" onClick={stopRow}>
        <i
          className="fa fa-minus"
          aria-label="削除"
          style={{ cursor: "pointer" }}
          onClick={async (e) => {
            e.stopPropagation();
            const idStr = formatIdWithZeroPadding(customer.id);
            if (
              window.confirm(
                `本当に「${idStr}」を削除しますか？\n※削除したデータは元に戻せません。`,
              )
            ) {
              await onDelete(customer.id);
              alert(`ID: ${idStr} を削除しました。`);
            }
          }}
        ></i>
      </li>
    </ul>
  );
});

function CustomerListPageContent() {
  const [loadingText, setLoadingText] = useState("");
  const [customers, setCustomers] = useState([]);
  const [studios, setStudios] = useState([]);
  const [lessonOptions, setLessonOptions] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [filterStudio, setFilterStudio] = useState("");
  const [filterCourse, setFilterCourse] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCreatedFrom, setFilterCreatedFrom] = useState("");
  const [filterCreatedTo, setFilterCreatedTo] = useState("");
  const [searchText, setSearchText] = useState("");
  // ソートは sortKey + sortOrder の 1 組（1 種類のみ有効）
  const [sortKey, setSortKey] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // 新規追加モード。選択中 customer とは排他（同じ右パネルを使うため）
  const [isCreating, setIsCreating] = useState(false);
  // 新規時のレッスン候補は下書きの店舗で引く（選択中 customer が居ないため）
  const [creatingStudioId, setCreatingStudioId] = useState("");

  const apiUrl = process.env.REACT_APP_API_URL;

  const fetchCustomers = useCallback(async () => {
    setLoadingText("読み込み中");
    try {
      // status 未指定なら解約(退会)を除外して取得（一覧を軽量化）
      const query = filterStatus ? `?status=${filterStatus}` : "";
      const response = await fetch(`${apiUrl}/api/get_customers/${query}`);
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      // No.は表示順にフロント側で採番する(下部 rows で index+1)。フィルタ/ソートの並びに応じて振り直す。
      setCustomers(data.customers || []);
    } catch (error) {
      setCustomers([]);
    }
    setLoadingText("");
  }, [apiUrl, filterStatus]);

  const fetchStudios = useCallback(async () => {
    try {
      const response = await fetch(`${apiUrl}/api/get_studios_list/`);
      if (!response.ok) return;
      const data = await response.json();
      setStudios(data.studios || []);
    } catch {
      // 失敗時は customers から派生する getUniqueStudios にフォールバック（編集 select は空）
    }
  }, [apiUrl]);

  // 選択中 customer（新規時は下書き）の店舗に応じてレッスン候補をfetch
  const optionsStudioId = isCreating
    ? creatingStudioId
    : selectedCustomer?.studio_id;
  useEffect(() => {
    if (!optionsStudioId) {
      setLessonOptions([]);
      return;
    }
    fetch(`${apiUrl}/api/get_customer_lesson_options/?studio_id=${optionsStudioId}`)
      .then((r) => (r.ok ? r.json() : { options: [] }))
      .then((d) => setLessonOptions(d.options || []))
      .catch(() => setLessonOptions([]));
  }, [apiUrl, optionsStudioId]);

  // ステータス変更で fetchCustomers の identity が変わり再取得される
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchStudios();
  }, [fetchStudios]);

  const handleReset = async () => {
    setFilterStudio("");
    setFilterCourse("");
    setFilterStatus("active");
    setFilterCreatedFrom("");
    setFilterCreatedTo("");
    setSearchText("");
    setSortKey("");
    setSortOrder("");
    await fetchCustomers();
  };

  // ソートボタン: 同じ組み合わせを再クリックなら OFF、別組み合わせなら ON へ切り替え
  const handleSortClick = (key, order) => {
    if (sortKey === key && sortOrder === order) {
      setSortKey("");
      setSortOrder("");
    } else {
      setSortKey(key);
      setSortOrder(order);
    }
  };
  const isSortOn = (key, order) => sortKey === key && sortOrder === order;

  // 旧来の単純更新（チェックボックスのインライン用、残存）
  // useCallback で参照を固定し、行の memo 化（MemoInput）を有効にする。
  // selectedCustomer は関数型更新で読むためクロージャ依存に含めない。
  const patchCustomer = useCallback(
    async (customerId, fields) => {
      const response = await fetch(
        `${apiUrl}/api/update_customer/${customerId}/`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "更新に失敗しました");
      }
      setCustomers((prev) =>
        prev.map((c) => (c.id === customerId ? { ...c, ...fields } : c)),
      );
      setSelectedCustomer((prev) =>
        prev && prev.id === customerId ? { ...prev, ...fields } : prev,
      );
    },
    [apiUrl],
  );

  // 備考の保存（一覧のインライン入力 → update_customer の memo を更新）
  const updateMemo = useCallback(
    async (customerId, memo) => {
      await patchCustomer(customerId, { memo });
    },
    [patchCustomer],
  );

  // 編集モードからの一括保存
  const saveCustomer = useCallback(
    async (customerId, payload) => {
      const response = await fetch(
        `${apiUrl}/api/update_customer/${customerId}/`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "更新に失敗しました");
      }
      // 楽観更新：詳細表示で必要な studio_name は studios から引き直す
      const studio = studios.find((s) => s.id === payload.studio_id);
      const updated = {
        ...payload,
        studio_name: studio?.name || "",
      };
      setCustomers((prev) =>
        prev.map((c) => (c.id === customerId ? { ...c, ...updated } : c)),
      );
      setSelectedCustomer((prev) =>
        prev?.id === customerId ? { ...prev, ...updated } : prev,
      );
    },
    [apiUrl, studios],
  );

  const addLesson = useCallback(
    async (customerId, placementId) => {
      const response = await fetch(
        `${apiUrl}/api/add_customer_lesson/${customerId}/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ placement_id: placementId }),
        },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "追加に失敗しました");
      }
      const data = await response.json();
      const newLesson = data.lesson;
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === customerId
            ? { ...c, lessons: [...(c.lessons || []), newLesson] }
            : c,
        ),
      );
      setSelectedCustomer((prev) =>
        prev?.id === customerId
          ? { ...prev, lessons: [...(prev.lessons || []), newLesson] }
          : prev,
      );
    },
    [apiUrl],
  );

  const deleteLesson = useCallback(
    async (customerId, lessonId) => {
      const response = await fetch(
        `${apiUrl}/api/delete_customer_lesson/${customerId}/${lessonId}/`,
        { method: "DELETE" },
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "削除に失敗しました");
      }
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === customerId
            ? {
                ...c,
                lessons: (c.lessons || []).filter((l) => l.id !== lessonId),
              }
            : c,
        ),
      );
      setSelectedCustomer((prev) =>
        prev?.id === customerId
          ? {
              ...prev,
              lessons: (prev.lessons || []).filter((l) => l.id !== lessonId),
            }
          : prev,
      );
    },
    [apiUrl],
  );

  const deleteCustomer = useCallback(
    async (customerId) => {
      setLoadingText("削除中");
      try {
        const response = await fetch(
          `${apiUrl}/api/delete_customer/${customerId}/`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
          },
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "削除に失敗しました");
        }
        // selectedCustomer は関数型更新で読み、依存に含めない(参照を固定=行の memo 維持)
        setSelectedCustomer((prev) => (prev?.id === customerId ? null : prev));
        await fetchCustomers();
      } catch (error) {
        alert(`削除に失敗しました: ${error.message}`);
      } finally {
        setLoadingText("");
      }
    },
    [apiUrl, fetchCustomers],
  );

  // 管理画面からの新規登録。会員と申込レッスンは API 側で1トランザクション作成。
  const createCustomer = useCallback(
    async (payload) => {
      setLoadingText("登録中");
      try {
        const response = await fetch(`${apiUrl}/api/create_customer/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "登録に失敗しました");
        setIsCreating(false);
        setCreatingStudioId("");
        await fetchCustomers();
      } finally {
        setLoadingText("");
      }
    },
    [apiUrl, fetchCustomers],
  );

  // 行クリックで詳細パネル開閉。selectedCustomer は関数型更新で読み参照を固定。
  const handleRowClick = useCallback((customer) => {
    setIsCreating(false);
    setSelectedCustomer((prev) => (prev?.id === customer.id ? null : customer));
  }, []);

  const handleStartCreate = useCallback(() => {
    setSelectedCustomer(null);
    setCreatingStudioId("");
    setIsCreating(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedCustomer(null);
    setIsCreating(false);
    setCreatingStudioId("");
  }, []);

  // studio 一覧：API が成功すればそちら、失敗時は customers から派生
  const studiosForSelect = useMemo(() => {
    if (studios.length > 0) return studios;
    const map = new Map();
    customers.forEach((c) => {
      if (c.studio_id && c.studio_name) map.set(c.studio_id, c.studio_name);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "ja"));
  }, [studios, customers]);

  const uniqueCourses = useMemo(() => {
    const set = new Set(customers.map((c) => c.course_name).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ja"));
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    let filtered = customers;
    if (filterStudio) {
      filtered = filtered.filter((c) => c.studio_id === parseInt(filterStudio));
    }
    if (filterCourse) {
      filtered = filtered.filter((c) => c.course_name === filterCourse);
    }
    if (filterStatus && filterStatus !== "all") {
      filtered = filtered.filter((c) => (c.status || "active") === filterStatus);
    }
    if (filterCreatedFrom || filterCreatedTo) {
      filtered = filtered.filter((c) => {
        const created = (c.created_at || "").slice(0, 10);
        if (!created) return false;
        if (filterCreatedFrom && created < filterCreatedFrom) return false;
        if (filterCreatedTo && created > filterCreatedTo) return false;
        return true;
      });
    }
    if (searchText) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter((c) => {
        const fullName = `${c.last_name} ${c.first_name}`.toLowerCase();
        const kana = `${c.last_kana} ${c.first_kana}`.toLowerCase();
        return (
          fullName.includes(lower) ||
          kana.includes(lower) ||
          (c.email || "").toLowerCase().includes(lower) ||
          (c.phone_number || "").toLowerCase().includes(lower)
        );
      });
    }
    if (sortKey && sortOrder) {
      filtered = [...filtered].sort((a, b) => {
        // 名前: ふりがな + localeCompare
        if (sortKey === "name") {
          const nameA = `${a.last_kana || ""} ${a.first_kana || ""}`;
          const nameB = `${b.last_kana || ""} ${b.first_kana || ""}`;
          const cmp = nameA.localeCompare(nameB, "ja");
          return sortOrder === "asc" ? cmp : -cmp;
        }
        // 日付系: ISO / YYYY-MM-DD ともに文字列比較で正しく並ぶ
        const valA = a[sortKey] || "";
        const valB = b[sortKey] || "";
        // 空値は常に末尾
        if (!valA && !valB) return 0;
        if (!valA) return 1;
        if (!valB) return -1;
        // 年齢ソートは UI ラベルと方向を合わせるため birth_date を反転
        // 「年齢 昇順」= 若い順 = birth_date 降順
        const flip = sortKey === "birth_date";
        const baseAsc = sortOrder === "asc";
        const asc = flip ? !baseAsc : baseAsc;
        if (valA < valB) return asc ? -1 : 1;
        if (valA > valB) return asc ? 1 : -1;
        return 0;
      });
    } else {
      // 並び替え未指定時は登録の新しい順(id降順)を既定表示にする(新規レコードが上、この並びに上から No.を振る)
      filtered = [...filtered].sort((a, b) => b.id - a.id);
    }
    return filtered;
  }, [
    customers,
    filterStudio,
    filterCourse,
    filterStatus,
    filterCreatedFrom,
    filterCreatedTo,
    searchText,
    sortKey,
    sortOrder,
  ]);

  // 一覧の行JSXをメモ化。selectedCustomer に依存させないことで、パネル開閉時に
  // React が547要素を再処理するのを防ぐ（要素参照が同じなら reconciliation を bailout）。
  const rows = useMemo(
    () =>
      filteredCustomers.map((customer, index) => (
        <CustomerRow
          key={customer.id}
          customer={customer}
          displayNo={index + 1}
          onRowClick={handleRowClick}
          onPatch={patchCustomer}
          onUpdateMemo={updateMemo}
          onDelete={deleteCustomer}
        />
      )),
    [filteredCustomers, handleRowClick, patchCustomer, updateMemo, deleteCustomer],
  );

  // 選択ハイライトは DOM を直接更新（一覧全体の再レンダーを避けるため）
  useEffect(() => {
    const container = document.querySelector(".dataBox");
    if (!container) return;
    container
      .querySelectorAll(".row-clickable.selected")
      .forEach((el) => el.classList.remove("selected"));
    if (selectedCustomer) {
      container
        .querySelector(`[data-customer-id="${selectedCustomer.id}"]`)
        ?.classList.add("selected");
    }
  }, [selectedCustomer, rows]);

  return (
    <div
      className={`customers-layout ${
        selectedCustomer || isCreating ? "panel-open" : ""
      }`}
    >
      <div className="customers-main">
        <div className="filterSectionWrap">
          <div className="filterSectionInner">
            <div className="buttonWrap">
              <div className="left">
                <button className="add-customer" onClick={handleStartCreate}>
                  ＋ 新規会員を追加
                </button>
                <button
                  className="filter"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  フィルター・絞り込み
                </button>
              </div>
              <button className="sort-reset" onClick={handleReset}>
                リセット
              </button>
            </div>
            <div className={`sort-setting ${isFilterOpen ? "on" : ""}`}>
              <dl>
                <dt>名前</dt>
                <dd>
                  <button
                    className={isSortOn("name", "asc") ? "on" : ""}
                    onClick={() => handleSortClick("name", "asc")}
                  >
                    昇順
                  </button>
                  <button
                    className={isSortOn("name", "desc") ? "on" : ""}
                    onClick={() => handleSortClick("name", "desc")}
                  >
                    降順
                  </button>
                </dd>
              </dl>
              <dl>
                <dt>申込日</dt>
                <dd>
                  <button
                    className={isSortOn("created_at", "asc") ? "on" : ""}
                    onClick={() => handleSortClick("created_at", "asc")}
                  >
                    昇順
                  </button>
                  <button
                    className={isSortOn("created_at", "desc") ? "on" : ""}
                    onClick={() => handleSortClick("created_at", "desc")}
                  >
                    降順
                  </button>
                </dd>
              </dl>
              <dl>
                <dt>開始日</dt>
                <dd>
                  <button
                    className={isSortOn("lesson_start_date", "asc") ? "on" : ""}
                    onClick={() => handleSortClick("lesson_start_date", "asc")}
                  >
                    昇順
                  </button>
                  <button
                    className={
                      isSortOn("lesson_start_date", "desc") ? "on" : ""
                    }
                    onClick={() => handleSortClick("lesson_start_date", "desc")}
                  >
                    降順
                  </button>
                </dd>
              </dl>
              <dl>
                <dt>年齢</dt>
                <dd>
                  <button
                    className={isSortOn("birth_date", "asc") ? "on" : ""}
                    onClick={() => handleSortClick("birth_date", "asc")}
                  >
                    昇順
                  </button>
                  <button
                    className={isSortOn("birth_date", "desc") ? "on" : ""}
                    onClick={() => handleSortClick("birth_date", "desc")}
                  >
                    降順
                  </button>
                </dd>
              </dl>
              <dl>
                <dt>店舗</dt>
                <dd>
                  <select
                    value={filterStudio}
                    onChange={(e) => setFilterStudio(e.target.value)}
                  >
                    <option value="">すべて</option>
                    {studiosForSelect.map((studio) => (
                      <option key={studio.id} value={studio.id}>
                        {studio.name}
                      </option>
                    ))}
                  </select>
                </dd>
              </dl>
              <dl>
                <dt>コース</dt>
                <dd>
                  <select
                    value={filterCourse}
                    onChange={(e) => setFilterCourse(e.target.value)}
                  >
                    <option value="">すべて</option>
                    {uniqueCourses.map((course, i) => (
                      <option key={i} value={course}>
                        {course}
                      </option>
                    ))}
                  </select>
                </dd>
              </dl>
              <dl>
                <dt>ステータス</dt>
                <dd>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="all">すべて</option>
                    <option value="active">在籍</option>
                    <option value="pause">休会</option>
                    <option value="cancel">退会</option>
                  </select>
                </dd>
              </dl>
              <dl className="date-range">
                <dt>申込日範囲</dt>
                <dd>
                  <input
                    type="date"
                    value={filterCreatedFrom}
                    onChange={(e) => setFilterCreatedFrom(e.target.value)}
                  />
                  <span className="range-sep">〜</span>
                  <input
                    type="date"
                    value={filterCreatedTo}
                    onChange={(e) => setFilterCreatedTo(e.target.value)}
                  />
                </dd>
              </dl>
              <dl className="saerch">
                <dt>
                  <i className="fa-thin fa-magnifying-glass"></i>
                </dt>
                <dd>
                  <input
                    type="text"
                    placeholder="名前 / ふりがな / メールアドレス / 電話番号で検索"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="dataBoxWrap">
          <div className="dataBox">
            <ul>
              <li className="ttl">
                <p>No.</p>
              </li>
              <li className="ttl">
                <p>ID</p>
              </li>
              <li className="ttl">
                <p>氏名</p>
              </li>
              <li className="ttl">
                <p>ふりがな</p>
              </li>
              <li className="ttl">
                <p>年齢</p>
              </li>
              <li className="ttl">
                <p>店舗</p>
              </li>
              <li className="ttl">
                <p>コース</p>
              </li>
              <li className="ttl">
                <p>開始日</p>
              </li>
              <li className="ttl">
                <p>初回料金</p>
              </li>
              <li className="ttl">
                <p>申込日</p>
              </li>
              <li className="ttl">
                <p>クーポン</p>
              </li>
              <li className="ttl">
                <p>口コミ</p>
              </li>
              <li className="ttl">
                <p>銀行</p>
              </li>
              <li className="ttl">
                <p>口座登録</p>
              </li>
              <li className="ttl">
                <p>備考</p>
              </li>
              <li className="ttl">
                <p>メール</p>
              </li>
              <li className="ttl">
                <p>電話</p>
              </li>
              <li className="ttl">
                <p>削除</p>
              </li>
            </ul>

            {rows}
          </div>
        </div>
      </div>

      <CustomerDetail
        // 新規↔編集の切替で state を持ち越さないよう作り直す
        key={isCreating ? "new" : selectedCustomer?.id || "none"}
        customer={isCreating ? NEW_CUSTOMER : selectedCustomer}
        isNew={isCreating}
        studios={studiosForSelect}
        lessonOptions={lessonOptions}
        onClose={handleCloseDetail}
        onSave={saveCustomer}
        onCreate={createCustomer}
        onStudioChange={setCreatingStudioId}
        onAddLesson={addLesson}
        onDeleteLesson={deleteLesson}
        formatDate={formatDate}
        calcAge={calcAge}
      />

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CustomerListPage() {
  return <CustomerListPageContent />;
}
